import React, { useId, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faXmark } from "@fortawesome/free-solid-svg-icons";

export const ORGANIZATION_TABLE_FIELDS = Object.freeze({
    FaceDetection: ["train_table"],
    Yolov7_OD: ["train_table", "val_table"],
    Yolov8s_OD: ["train_table", "val_table"],
    YOLOWorld_OD: ["train_table", "val_table"],
    Yolov7_kpts: ["train_table", "val_table"],
    FR_arcface: ["train_table"],
    Liveness: ["datafile", "test_datafile"],
    AudioResnet: ["train_table"],
    Evaluation: ["eval_table"],
});

export const TABLE_INPUT_PLACEHOLDER =
    "ETL table name or data_service SilverTables/... path";

export function isOrganizationTableField(modelType, fieldName) {
    return (ORGANIZATION_TABLE_FIELDS[modelType] || []).includes(fieldName);
}

export function normalizeTableValues(value) {
    const values = Array.isArray(value) ? value : String(value || "").split(",");
    return [...new Set(values.map((item) => String(item).trim()).filter(Boolean))];
}

function OrganizationTableInput({
    value,
    options = [],
    onChange,
    loading = false,
    fieldName,
    placeholder = TABLE_INPUT_PLACEHOLDER,
}) {
    const [draft, setDraft] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const inputRef = useRef(null);
    const listboxId = useId();
    const selectedTables = useMemo(() => normalizeTableValues(value), [value]);
    const availableTables = useMemo(() => {
        const query = draft.trim().toLowerCase();
        return normalizeTableValues(options)
            .filter((table) => !selectedTables.includes(table))
            .filter((table) => !query || table.toLowerCase().includes(query))
            .sort((left, right) => left.localeCompare(right));
    }, [draft, options, selectedTables]);

    const updateValue = (tables) => onChange(normalizeTableValues(tables).join(","));

    const addTables = (rawValue) => {
        const additions = normalizeTableValues(rawValue);
        if (additions.length > 0) {
            updateValue([...selectedTables, ...additions]);
        }
        setDraft("");
        setActiveIndex(-1);
    };

    const removeTable = (tableToRemove) => {
        updateValue(selectedTables.filter((table) => table !== tableToRemove));
    };

    const handleKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex((current) =>
                Math.min(current + 1, availableTables.length - 1)
            );
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((current) => Math.max(current - 1, 0));
        } else if (event.key === "Enter" || event.key === ",") {
            event.preventDefault();
            if (activeIndex >= 0 && availableTables[activeIndex]) {
                addTables(availableTables[activeIndex]);
            } else {
                addTables(draft);
            }
        } else if (event.key === "Backspace" && !draft && selectedTables.length > 0) {
            removeTable(selectedTables[selectedTables.length - 1]);
        } else if (event.key === "Escape") {
            setIsOpen(false);
            setActiveIndex(-1);
        }
    };

    return (
        <div className="train-table-picker">
            <div
                className={`train-tag-input-box${isOpen ? " is-focused" : ""}`}
                onClick={() => inputRef.current?.focus()}
            >
                {selectedTables.map((table) => (
                    <span key={table} className="train-label-tag">
                        {table}
                        <button
                            type="button"
                            className="train-tag-remove"
                            onClick={(event) => {
                                event.stopPropagation();
                                removeTable(table);
                            }}
                            aria-label={`Remove ${table}`}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    className="train-tag-text-input"
                    value={draft}
                    onChange={(event) => {
                        setDraft(event.target.value);
                        setIsOpen(true);
                        setActiveIndex(-1);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onBlur={() => {
                        addTables(draft);
                        setIsOpen(false);
                    }}
                    onKeyDown={handleKeyDown}
                    aria-label={fieldName}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    placeholder={placeholder}
                />
                <button
                    type="button"
                    className="train-table-toggle"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                        setIsOpen((open) => !open);
                        inputRef.current?.focus();
                    }}
                    title="Show organization tables"
                    aria-label="Show organization tables"
                >
                    <FontAwesomeIcon icon={faChevronDown} />
                </button>
            </div>
            {isOpen && (
                <div id={listboxId} className="train-table-options" role="listbox">
                    {loading ? (
                        <div className="train-table-options-empty">Loading...</div>
                    ) : availableTables.length > 0 ? (
                        availableTables.map((table, index) => (
                            <button
                                key={table}
                                type="button"
                                role="option"
                                aria-selected={index === activeIndex}
                                className={`train-table-option${index === activeIndex ? " is-active" : ""}`}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                    addTables(table);
                                    inputRef.current?.focus();
                                }}
                            >
                                {table}
                            </button>
                        ))
                    ) : (
                        <div className="train-table-options-empty">No matching tables</div>
                    )}
                </div>
            )}
        </div>
    );
}

export default OrganizationTableInput;
